import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";

import { Button } from "@ps-design-system/components/Button";
import { Text } from "@ps-design-system/components/Text";
import XIcon from "@ps-design-system/icons/X/X24";

import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import {
  trackReferVendorButtonClicked,
  trackVendorRecommendationBannerDismissed,
} from "../analytics/host-hub-analytics";

interface VendorRecommendationBannerProps {
  dismiss: () => void;
}

const VendorRecommendationBanner = ({
  dismiss,
}: VendorRecommendationBannerProps) => {
  const ssoid = useSelector(selectProfileSSOid);

  const handleCtaClick = () => {
    trackReferVendorButtonClicked({
      buttonName: "refer a vendor",
      buttonLocation: "host hub",
    });
    window.open(
      "https://hosts.trypeerspace.com/vendor-recommendations",
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleDismiss = () => {
    trackVendorRecommendationBannerDismissed({ ssoid });
    dismiss();
  };

  return (
    <div
      className="tw-relative tw-flex tw-flex-col tw-gap-[20px] md:tw-gap-[24px] tw-overflow-hidden tw-rounded-[12px] tw-p-[24px] tw-text-brand-white md:tw-flex-col-reverse lg:tw-flex-row lg:tw-items-center lg:tw-gap-[32px] lg:tw-p-[32px]"
      style={{
        background:
          "linear-gradient(249deg, rgba(0, 0, 0, 0.55) 0.49%, rgba(0, 0, 0, 0.00) 45.81%), linear-gradient(78deg, rgba(0, 0, 0, 0.55) 12.58%, rgba(0, 0, 0, 0.00) 81.3%), linear-gradient(0deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.50) 100%), var(--purple-purple-900, #5627B0)",
      }}
    >
      <button
        aria-label="Dismiss vendor recommendation banner"
        className="tw-absolute tw-right-[16px] tw-top-[16px] tw-flex tw-h-[32px] tw-w-[32px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-brand-white"
        onClick={handleDismiss}
        type="button"
      >
        <XIcon />
      </button>

      <div className="tw-flex tw-flex-col md:tw-flex-row-reverse md:tw-gap-16 lg:tw-flex-col-reverse tw-items-center xl:tw-flex-row">
        <img
          alt="Various Peerspace vendors"
          aria-hidden="true"
          className="tw-mx-auto tw-block tw-w-full md:tw-w-[50%] lg:tw-hidden"
          src="https://img.peerspace.com/image/upload/v1787767568/Mobile_Vendor_Network_2x_bdcclh.png"
        />

        <div className="tw-flex tw-flex-1 tw-flex-col md:tw-gap-[16px] lg:tw-pr-0">
          <div className="tw-flex tw-flex-col tw-gap-[8px]">
            <Text
              element="h3"
              typeface="headline-3-m"
              className="tw-mt-16 tw-mb-0 tw-text-[24px] tw-font-semibold tw-leading-[32px] tw-text-brand-white"
            >
              <FormattedMessage
                id="host_hub.vendor_recommendation.title"
                defaultMessage="Who's in your network?"
              />
            </Text>
            <Text
              element="p"
              className="tw-mb-24 tw-text-brand-white sm:tw-max-w-[400px]"
            >
              <FormattedMessage
                id="host_hub.vendor_recommendation.desc"
                defaultMessage="Recommend vendors you trust and love working with. We'll invite them to join Peerspace where they'll show up as a preferred collaborator on your future bookings."
              />
            </Text>
          </div>
          <div className="tw-flex">
            <Button
              appearance="purple"
              className="tw-w-full md:tw-w-auto lg:tw-w-full xl:tw-w-auto md:tw-mb-16"
              onClick={handleCtaClick}
              variant="default"
            >
              <Text typeface="body-2-m">
                <FormattedMessage
                  id="host_hub.vendor_recommendation.cta_btn"
                  defaultMessage="Refer a vendor"
                />
              </Text>
            </Button>
          </div>
        </div>

        <img
          alt=""
          aria-hidden="true"
          className="tw-relative tw-hidden tw-max-w-[300px] lg:tw-block xl:tw--right-32"
          src="https://img.peerspace.com/image/upload/v1787767569/Desktop_Vendor_Network_2x_1_bawjck.png"
        />
      </div>
    </div>
  );
};

export default VendorRecommendationBanner;
